import { aiWidgetAliases, AiWidgetAlias } from './aiWidgetActions';
import {
  conversationBalanceMetricNames,
  conversationBalanceMetricUnits,
  conversationBalanceRoles,
  dispositionChangeBuckets,
} from './aiWidgetResponse.types';

/** Contract-aware renderability guards with a legacy fallback. */

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isNonNegativeInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isInteger(value) && value >= 0;

export const isPercentageValue = (value: unknown): value is number =>
  isNonNegativeInteger(value) && value <= 100;

/** Accept independently rounded totals and all-zero distributions. */
export const AI_WIDGET_PERCENTAGE_TOTAL_TOLERANCE = { min: 99, max: 101 } as const;

export const isAllZero = (values: number[]): boolean => values.every((value) => value === 0);

export const isWithinPercentageTotalTolerance = (values: number[]): boolean => {
  if (values.length === 0) return false;
  if (isAllZero(values)) return true;

  const total = values.reduce((sum, value) => sum + value, 0);
  return (
    total >= AI_WIDGET_PERCENTAGE_TOTAL_TOLERANCE.min &&
    total <= AI_WIDGET_PERCENTAGE_TOTAL_TOLERANCE.max
  );
};

const churnRiskBuckets = ['Low risk', 'Medium risk', 'High risk', 'Critical risk'] as const;
const customerEffortBuckets = ['Low', 'Medium', 'High'] as const;

const hasExpectedBuckets = (values: string[], expected: readonly string[]): boolean => {
  const expectedSet = new Set(expected);
  return (
    values.length === expected.length &&
    new Set(values).size === expected.length &&
    values.every((value) => expectedSet.has(value))
  );
};

const isRenderableConversationBalanceSeriesPoint = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.role === 'string' &&
  conversationBalanceRoles.includes(value.role as (typeof conversationBalanceRoles)[number]) &&
  isPercentageValue(value.value) &&
  (value.rawValue === undefined || (isFiniteNumber(value.rawValue) && value.rawValue >= 0));

const isRenderableConversationBalanceMetric = (value: unknown): boolean => {
  if (!isRecord(value) || !Array.isArray(value.series)) return false;

  const nameValid =
    typeof value.name === 'string' &&
    conversationBalanceMetricNames.includes(
      value.name as (typeof conversationBalanceMetricNames)[number]
    );

  if (!nameValid || !isNonEmptyString(value.label)) return false;
  if (value.series.length !== conversationBalanceRoles.length) return false;
  if (!value.series.every(isRenderableConversationBalanceSeriesPoint)) return false;

  const series = value.series as { role: string; value: number; rawValue?: number }[];
  const hasRawValues = series.every((point) => point.rawValue !== undefined);
  const hasNoRawValues = series.every((point) => point.rawValue === undefined);
  const hasValidUnit =
    typeof value.unit === 'string' &&
    conversationBalanceMetricUnits.includes(
      value.unit as (typeof conversationBalanceMetricUnits)[number]
    );
  const isLegacyMetric = value.unit === undefined && hasNoRawValues;
  const isEnrichedMetric = hasValidUnit && hasRawValues;
  if (!isLegacyMetric && !isEnrichedMetric) return false;

  if (
    !hasExpectedBuckets(
      series.map((point) => point.role),
      conversationBalanceRoles
    )
  )
    return false;

  const values = series.map((point) => point.value);
  return isWithinPercentageTotalTolerance(values);
};

const isRenderableConversationBalance = (data: unknown): boolean => {
  if (!isRecord(data) || data.isPercentage !== true) return false;
  if (
    !Array.isArray(data.metrics) ||
    data.metrics.length !== conversationBalanceMetricNames.length
  ) {
    return false;
  }
  if (!data.metrics.every(isRenderableConversationBalanceMetric)) return false;

  return hasExpectedBuckets(
    (data.metrics as { name: string }[]).map((metric) => metric.name),
    conversationBalanceMetricNames
  );
};

const isRenderableNamedSeriesPoint = (value: unknown): boolean =>
  isRecord(value) && isNonEmptyString(value.name) && isPercentageValue(value.value);

const isRenderableChurnRiskDistribution = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  if (data.isPercentage !== true || typeof data.showLabels !== 'boolean') return false;
  if (!Array.isArray(data.series) || data.series.length !== churnRiskBuckets.length) return false;
  if (!data.series.every(isRenderableNamedSeriesPoint)) return false;

  const series = data.series as { name: string; value: number }[];
  if (
    !hasExpectedBuckets(
      series.map((point) => point.name),
      churnRiskBuckets
    )
  )
    return false;

  const values = series.map((point) => point.value);
  return isWithinPercentageTotalTolerance(values);
};

const isRenderableCustomerEffortDistribution = (value: unknown): boolean => {
  if (
    !isRecord(value) ||
    !Array.isArray(value.series) ||
    value.series.length !== customerEffortBuckets.length
  ) {
    return false;
  }
  if (value.isPercentage !== true) return false;
  if (!isPercentageValue(value.high_effort_pct)) return false;
  if (!value.series.every(isRenderableNamedSeriesPoint)) return false;

  const series = value.series as { name: string; value: number }[];
  if (
    !hasExpectedBuckets(
      series.map((point) => point.name),
      customerEffortBuckets
    )
  )
    return false;

  const values = series.map((point) => point.value);
  return isWithinPercentageTotalTolerance(values);
};

const isRenderableCustomerEffortTouchpoint = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.touchpoint) &&
  isRenderableCustomerEffortDistribution(value);

const isRenderableCustomerEffortBreakdown = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  if (!isRenderableCustomerEffortDistribution(data.overall)) return false;
  if (!Array.isArray(data.by_touchpoint)) return false;

  return data.by_touchpoint.every(isRenderableCustomerEffortTouchpoint);
};

const isRenderableFrictionSignalItem = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.label) &&
  isNonNegativeInteger(value.count) &&
  isPercentageValue(value.pct);

const isRenderableFrictionSignalsFrequency = (data: unknown): boolean => {
  if (!isRecord(data)) return false;
  if (!Array.isArray(data.items) || data.items.length === 0) return false;
  if (!isNonNegativeInteger(data.total_sessions)) return false;
  if (!data.items.every(isRenderableFrictionSignalItem)) return false;

  const counts = (data.items as { count: number }[]).map((item) => item.count);
  const pcts = (data.items as { pct: number }[]).map((item) => item.pct);

  // Partial signal lists do not need to total 100%.
  if (isAllZero(counts)) return isAllZero(pcts);
  return true;
};

const isDispositionChangeBucket = (
  value: unknown
): value is (typeof dispositionChangeBuckets)[number] =>
  typeof value === 'string' &&
  dispositionChangeBuckets.includes(value as (typeof dispositionChangeBuckets)[number]);

const isRenderableDispositionChangeSeriesPoint = (value: unknown): boolean =>
  isRecord(value) && isDispositionChangeBucket(value.name) && isPercentageValue(value.value);

const isRenderableDispositionChangeReasonItem = (value: unknown): boolean =>
  isRecord(value) && isNonEmptyString(value.label) && isNonNegativeInteger(value.count);

const isRenderableDispositionChangeReasonGroup = (value: unknown): boolean =>
  isRecord(value) &&
  isDispositionChangeBucket(value.bucket) &&
  Array.isArray(value.items) &&
  value.items.every(isRenderableDispositionChangeReasonItem);

const isRenderableDispositionChangeDistribution = (data: unknown): boolean => {
  if (!isRecord(data) || data.isPercentage !== true) return false;
  if (!Array.isArray(data.series) || data.series.length !== dispositionChangeBuckets.length) {
    return false;
  }
  if (!data.series.every(isRenderableDispositionChangeSeriesPoint)) return false;

  const bucketsPresent = new Set((data.series as { name: string }[]).map((point) => point.name));
  if (bucketsPresent.size !== dispositionChangeBuckets.length) return false;

  const values = (data.series as { value: number }[]).map((point) => point.value);
  if (!isWithinPercentageTotalTolerance(values)) return false;

  if (!isDispositionChangeBucket(data.dominant_bucket)) return false;
  if (!isPercentageValue(data.dominant_pct)) return false;

  if (
    !Array.isArray(data.reasons) ||
    data.reasons.length !== dispositionChangeBuckets.length ||
    !data.reasons.every(isRenderableDispositionChangeReasonGroup)
  ) {
    return false;
  }

  const reasonBuckets = new Set(
    (data.reasons as { bucket: string }[]).map((group) => group.bucket)
  );
  return reasonBuckets.size === dispositionChangeBuckets.length;
};

/** Original CardWidget rules for non-AI aliases. */
export const hasLegacyRenderableWidgetData = (response: unknown): boolean => {
  if (!isRecord(response) || !response.data) return false;

  const {
    series,
    seriesData,
    corporatePulseStats,
    corporatePulseBottomStats,
    topFive,
    bottomFive,
    xData,
    data,
    textContent,
    styles,
  } = response.data as Record<string, unknown>;

  const textBlock = Boolean(textContent && styles);

  const corporatePulseStatsData =
    Array.isArray(corporatePulseStats) && corporatePulseStats.length > 0;

  const corporatePulseBottomStatsData =
    Array.isArray(corporatePulseBottomStats) && corporatePulseBottomStats.length > 0;

  const seriesValues = Array.isArray(series) && series.find((i: any) => i.value !== 0);

  return Boolean(
    (Array.isArray(series) && series.length > 0 && seriesValues) ||
      (Array.isArray(seriesData) && seriesData.length > 0) ||
      corporatePulseStatsData ||
      corporatePulseBottomStatsData ||
      textBlock ||
      (Array.isArray(topFive) && topFive.length > 0) ||
      (Array.isArray(bottomFive) && bottomFive.length > 0) ||
      (Array.isArray(xData) && xData.length > 0) ||
      (Array.isArray(data) && data.length > 0)
  );
};

const aiWidgetGuardsByAlias: Record<AiWidgetAlias, (data: unknown) => boolean> = {
  [aiWidgetAliases.CONVERSATION_BALANCE]: isRenderableConversationBalance,
  [aiWidgetAliases.CUSTOMER_EFFORT]: isRenderableCustomerEffortBreakdown,
  [aiWidgetAliases.DISPOSITION_CHANGE]: isRenderableDispositionChangeDistribution,
  [aiWidgetAliases.FRICTION_SIGNALS]: isRenderableFrictionSignalsFrequency,
  [aiWidgetAliases.CHURN_RISK_DISTRIBUTION]: isRenderableChurnRiskDistribution,
};

const isAiWidgetAlias = (alias: unknown): alias is AiWidgetAlias =>
  typeof alias === 'string' && alias in aiWidgetGuardsByAlias;

/** Uses the matching AI contract guard or the legacy rules. */
export const hasRenderableWidgetData = (alias: unknown, response: unknown): boolean => {
  if (!isRecord(response) || !('data' in response) || response.data == null) return false;

  if (isAiWidgetAlias(alias)) {
    return aiWidgetGuardsByAlias[alias](response.data);
  }

  return hasLegacyRenderableWidgetData(response);
};
